import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { DojoProvider } from "@/plugins/vibeDojo/DojoContext";
import { dojo } from "@/plugins/vibeDojo/dojoAudio";
import { usePublicSettings } from "@/hooks/useSettings";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DojoLayout } from "@/plugins/vibeDojo/DojoLayout";
import { PromoRail } from "@/components/PromoRail";
import { FamilySafeBar } from "@/components/FamilySafeBadge";
import { BetaCompanion } from "@/components/BetaCompanion";
import { BetaBanner } from "@/components/BetaBanner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Generate from "@/pages/Generate";
import CoursePlayer from "@/pages/CoursePlayer";
import Pricing from "@/pages/Pricing";
import Enterprise from "@/pages/Enterprise";
import Personalpedia from "@/pages/Personalpedia";
import FAQ from "@/pages/FAQ";
import Organization from "@/pages/Organization";
import OrgLibrary from "@/pages/OrgLibrary";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import AcceptableUse from "@/pages/AcceptableUse";
import GuestGenerate from "@/pages/GuestGenerate";
import GuestCoursePlayer from "@/pages/GuestCoursePlayer";
import PublicWatch from "@/pages/PublicWatch";
import Theater from "@/pages/Theater";
import ExpertSearch from "@/pages/ExpertSearch";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Support from "@/pages/Support";
import Admin from "@/pages/Admin";
import Dojo from "@/plugins/vibeDojo/Dojo";
import ControlCenter from "@/pages/admin/ControlCenter";
import AdminCommandCenter from "@/pages/admin/AdminCommandCenter";

// Dev-only: lazy-loaded and guarded so the switcher (incl. the test password)
// is dead-code-eliminated from the production bundle, not just hidden.
const IS_DEV = process.env.NODE_ENV !== "production";
const DevLoginSwitcher = IS_DEV
  ? lazy(() => import("@/components/DevLoginSwitcher").then((m) => ({ default: m.DevLoginSwitcher })))
  : null;

// Routes where the "From Learn Me" promo rail (owned ad space) is shown:
// the dashboard + all public-facing surfaces. Excludes auth/legal/admin/immersive pages.
const RAIL_ROUTES = ["/", "/dashboard", "/pricing", "/enterprise", "/experts", "/theater"];
const showRail = (p) =>
  RAIL_ROUTES.includes(p) || p.startsWith("/demo") || p.startsWith("/watch");

function RoutedArea({ children }) {
  const { pathname } = useLocation();
  const show = showRail(pathname);
  // Family Safe trust bar sitewide, except immersive Dojo + admin surfaces.
  const showSafeBar = !pathname.startsWith("/dojo") && !pathname.startsWith("/admin");
  return (
    <>
      <BetaBanner />
      <div className={show ? "xl:pr-[300px]" : undefined}>
        {children}
        {show && <PromoRail variant="inline" />}
        {showSafeBar && <FamilySafeBar />}
      </div>
      {show && <PromoRail variant="rail" />}
    </>
  );
}

function App() {
  const { data: settings } = usePublicSettings();
  const vibeOn = (settings?.plugins || []).find((p) => p.id === "dojo_vibe")?.enabled ?? true;
  useEffect(() => {
    const d = settings?.dojo;
    const base = process.env.REACT_APP_BACKEND_URL;
    dojo.setTrack(vibeOn && d?.has_track ? `${base}/api/dojo/track?v=${d.version || 0}` : null);
  }, [settings?.dojo?.has_track, settings?.dojo?.version, vibeOn]);
  return (
    <div className="App">
      <AuthProvider>
        <DojoProvider>
        <BrowserRouter>
          <DojoLayout enabled={vibeOn}>
          <RoutedArea>
            <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/acceptable-use" element={<AcceptableUse />} />
            <Route path="/demo" element={<GuestGenerate />} />
            <Route path="/demo/course/:id" element={<GuestCoursePlayer />} />
            <Route path="/watch/:id" element={<PublicWatch />} />
            <Route path="/theater" element={<Theater />} />
            <Route path="/experts" element={<ExpertSearch />} />
            <Route path="/enterprise" element={<Enterprise />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/dojo" element={<Dojo />} />
            <Route path="/learn" element={<ProtectedRoute><Generate /></ProtectedRoute>} />
            <Route path="/personalpedia" element={<ProtectedRoute><Personalpedia /></ProtectedRoute>} />
            <Route path="/faq" element={<ProtectedRoute><FAQ /></ProtectedRoute>} />
            <Route path="/organization" element={<ProtectedRoute><Organization /></ProtectedRoute>} />
            <Route path="/org/library" element={<ProtectedRoute><OrgLibrary /></ProtectedRoute>} />
            <Route path="/course/:id" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
            <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminCommandCenter /></ProtectedRoute>} />
            <Route path="/admin/legacy" element={<ProtectedRoute adminOnly><ControlCenter /></ProtectedRoute>} />
            <Route path="/admin/console" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
          </Routes>
          </RoutedArea>
          </DojoLayout>
          {IS_DEV && DevLoginSwitcher && (
            <Suspense fallback={null}>
              <DevLoginSwitcher />
            </Suspense>
          )}
          <BetaCompanion />
        </BrowserRouter>
        </DojoProvider>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
